import React from 'react'
import {
  Search,
  ListChecks,
  CalendarCheck,
  User,
  Inbox,
  Briefcase,
  Calendar,
} from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  useNotifications,
  fmtDateTimeSydney,
  apptId,
  requestId,
  Appt,
  RequestNotif,
} from '../notifications/NotificationsContext'

type Experience = 'client' | 'provider'

interface BottomDockProps {
  experience: Experience
}

type SheetMode = null | 'provider-requests' | 'provider-appointments'

export default function BottomDock({ experience }: BottomDockProps) {
  const nav = useNavigate()
  const loc = useLocation()
  const [sheet, setSheet] = React.useState<SheetMode>(null)
  const {
    navRole,
    appointments,
    requests,
    isApptSeen,
    isReqSeen,
    markApptSeen,
    markReqSeen,
    markAllAppointmentsSeen,
    markAllRequestsSeen,
  } = useNotifications()

  const clientRequests = React.useMemo(
    () => requests.filter(r => r.status !== 'PENDING'),
    [requests]
  )
  const providerRequests = React.useMemo(
    () => requests.filter(r => r.status === 'PENDING'),
    [requests]
  )

  const unseenAppts = React.useMemo(
    () => appointments.reduce((acc, a) => acc + (isApptSeen(apptId(a)) ? 0 : 1), 0),
    [appointments, isApptSeen]
  )

  const visibleClientRequests = React.useMemo(
    () => clientRequests.filter(req => !isReqSeen(requestId(req))),
    [clientRequests, isReqSeen]
  )
  const visibleProviderRequests = React.useMemo(
    () => providerRequests.filter(req => !isReqSeen(requestId(req))),
    [providerRequests, isReqSeen]
  )
  const clientRequestsBadge = experience === 'client'
    ? visibleClientRequests.length
    : 0
  const clientAppointmentsBadge = experience === 'client'
    ? unseenAppts
    : 0
  const providerRequestsBadge = experience === 'provider'
    ? visibleProviderRequests.length
    : 0
  const providerAppointmentsBadge = experience === 'provider'
    ? unseenAppts
    : 0

  const openSheet = (mode: SheetMode) => setSheet(mode)
  const closeSheet = () => setSheet(null)

  const buildAppointmentsHref = React.useCallback((view: 'patient' | 'provider', extra?: Record<string,string>) => {
    const params = new URLSearchParams()
    if (view === 'provider') params.set('view', 'provider')
    else params.set('view', 'client')
    if (extra) Object.entries(extra).forEach(([k, v]) => params.set(k, v))
    const qs = params.toString()
    const base = view === 'provider' ? '/appointments' : '/clientdashboard'
    return qs ? `${base}?${qs}` : base
  }, [])

  const openAppointment = (appt: Appt) => {
    const id = apptId(appt)
    if (!id) return
    markApptSeen(id)
    closeSheet()
    if (experience === 'provider') {
      const stack = appt.profession || appt.stack
      nav(stack ? `/calendar?stack=${stack}` : '/calendar')
      return
    }
    const extras: Record<string, string> = { apt: id }
    if (appt.profession) extras.stack = appt.profession
    nav(buildAppointmentsHref('patient', extras))
  }

  const openRequest = (req: RequestNotif) => {
    const id = requestId(req)
    markReqSeen(id)
    closeSheet()
    if (experience === 'provider' || req.status === 'PENDING') {
      nav(`/dashboard/${req.selectionId}?stack=${req.profession}`)
    } else {
      nav(`/requests/${req.selectionId}?stack=${req.profession}`)
    }
  }

  const clientTabs = [
    { key: 'match', label: 'Search', icon: <Search size={18} strokeWidth={1.6} />, path: '/clients' },
    { key: 'requests', label: 'Requests', icon: <ListChecks size={18} strokeWidth={1.6} />, path: '/requests' },
    { key: 'dashboard', label: 'Dashboard', icon: <CalendarCheck size={18} strokeWidth={1.6} />, path: '/clientdashboard' },
    { key: 'account', label: 'Account', icon: <User size={18} strokeWidth={1.6} />, path: '/account' },
  ]

  const providerTabs = [
    { key: 'inbox', label: 'Dashboard', icon: <Inbox size={18} strokeWidth={1.6} />, path: '/dashboard' },
    { key: 'schedule', label: 'Calendar', icon: <Calendar size={18} strokeWidth={1.6} />, path: '/calendar' },
    { key: 'work', label: 'Register', icon: <Briefcase size={18} strokeWidth={1.6} />, path: '/professionals' },
    { key: 'account', label: 'Account', icon: <User size={18} strokeWidth={1.6} />, path: '/account' },
  ]

  const tabs = experience === 'client' ? clientTabs : providerTabs

  const handleDockClick = (item: typeof tabs[number]) => {
    if (experience === 'client' && item.key === 'requests') {
      markAllRequestsSeen(visibleClientRequests.map(requestId))
      nav(item.path)
      return
    }
    if (experience === 'client' && item.key === 'dashboard') {
      markAllAppointmentsSeen(appointments.map(apptId).filter(Boolean) as string[])
      nav(item.path)
      return
    }
    if (experience === 'provider' && item.key === 'inbox' && providerRequestsBadge > 0) {
      openSheet('provider-requests')
      return
    }
    if (experience === 'provider' && item.key === 'schedule' && providerAppointmentsBadge > 0) {
      openSheet('provider-appointments')
      return
    }
    if (item.key === 'inbox' && (loc.pathname === '/dashboard' || loc.pathname.startsWith('/dashboard/'))) {
      window.dispatchEvent(new Event('inbox:refresh'))
      return
    }
    nav(item.path)
  }

  const handleMarkAll = () => {
    if (sheet === 'provider-requests') {
      markAllRequestsSeen(visibleProviderRequests.map(requestId))
      return
    }
    if (sheet === 'provider-appointments') {
      markAllAppointmentsSeen(appointments.map(apptId).filter(Boolean) as string[])
    }
  }

  const renderRequestList = (items: RequestNotif[], emptyLabel: string) => {
    const visible = items.filter(req => !isReqSeen(requestId(req)))
    if (!visible.length) {
      return <p className="notif-empty">{emptyLabel}</p>
    }
    return visible.map((req) => {
      const title =
        req.status === 'PENDING' ? 'New request' :
        req.status === 'FILLED' ? 'Request filled' :
        'Request open'
      return (
        <button
          key={`sheet-req-${requestId(req)}`}
          className="sheet-item"
          onClick={() => openRequest(req)}
        >
          <div>
            <div className="sheet-item-title">
              {title}{req.specialty ? ` • ${req.specialty}` : ''}
            </div>
            <div className="sheet-item-meta">{fmtDateTimeSydney(req.whenISO)}</div>
          </div>
          <span className="item-dot" aria-hidden />
        </button>
      )
    })
  }


  const describeAppointmentStatus = (appt: Appt) => {
    const key = (appt.status || '').toUpperCase()
    if (key === 'CANCELLED') {
      return { label: experience === 'provider' ? 'Cancelled by client' : 'Cancelled', className: 'is-cancelled' }
    }
    if (key === 'COMPLETED') {
      return { label: 'Completed', className: 'is-completed' }
    }
    return { label: 'Confirmed', className: 'is-confirmed' }
  }

  const renderAppointmentList = () => {
    const unseenAppointments = appointments.filter(appt => !isApptSeen(apptId(appt)))
    if (!unseenAppointments.length) {
      return <p className="notif-empty">No appointments yet.</p>
    }
    return unseenAppointments.map((appt) => {
      const id = apptId(appt)
      const statusDesc = describeAppointmentStatus(appt)
      return (
        <button
          key={`sheet-appt-${id}`}
          className="sheet-item"
          onClick={() => openAppointment(appt)}
        >
            <div>
              <div className="sheet-item-title">{appt.specialty || 'Consultation'}</div>
              <div className="sheet-item-meta" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span className={`sheet-item-pill ${statusDesc.className}`}>{statusDesc.label}</span>
                <span>{fmtDateTimeSydney(appt.chosenSlotISO)}</span>
              </div>
            </div>
          <span className="item-dot" aria-hidden />
        </button>
      )
    })
  }

  const sheetTitle = (() => {
    if (sheet === 'provider-requests') return 'Incoming requests'
    if (sheet === 'provider-appointments') return 'Scheduled consults'
    return ''
  })()
  const sheetSubtitle = (() => {
    if (sheet === 'provider-requests') return 'Patients requesting your availability'
    if (sheet === 'provider-appointments') return 'Confirmed bookings from clients'
    return ''
  })()

  const goToRequests = () => {
    closeSheet()
    if (loc.pathname === '/dashboard' || loc.pathname.startsWith('/dashboard/')) {
      window.dispatchEvent(new Event('inbox:refresh'))
      return
    }
    nav('/dashboard')
  }

  const goToCalendar = () => {
    closeSheet()
    nav('/calendar')
  }

  const renderSheetSections = () => {
    if (!sheet) return null
    if (sheet === 'provider-requests') {
      return (
        <section className="dock-sheet__section">
          <div className="dock-sheet__section-title">Incoming requests</div>
          {renderRequestList(providerRequests, 'No pending requests.')}
        </section>
      )
    }
    if (sheet === 'provider-appointments') {
      return (
        <section className="dock-sheet__section">
          <div className="dock-sheet__section-title">Appointments</div>
          {renderAppointmentList()}
        </section>
      )
    }
    return null
  }

  React.useEffect(() => {
    if (!sheet) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [sheet])

  return (
    <>
      <nav className="dock" aria-label={experience === 'client' ? 'Client actions' : 'Provider actions'}>
        <div className="dock-inner">
          {tabs.map((item) => {
            const active = (
              loc.pathname === item.path ||
              loc.pathname.startsWith(item.path + '/') ||
              (experience === 'client' && item.key === 'match' && loc.pathname.startsWith('/ai-search'))
            )
            const badgeCount =
              experience === 'client' && item.key === 'requests' ? clientRequestsBadge :
              experience === 'client' && item.key === 'dashboard' ? clientAppointmentsBadge :
              experience === 'provider' && item.key === 'inbox' ? providerRequestsBadge :
              experience === 'provider' && item.key === 'schedule' ? providerAppointmentsBadge :
              0
            const showBadge = badgeCount > 0
            return (
              <button
                key={item.key}
                type="button"
                className={`dock-item${active ? ' is-active' : ''}${showBadge ? ' has-badge' : ''}`}
                onClick={() => handleDockClick(item)}
              >
                <span className="dock-icon" aria-hidden>{item.icon}</span>
                <span className="dock-label">{item.label}</span>
                {showBadge && (
                  <span className="dock-badge" aria-hidden>
                    {badgeCount > 9 ? '9+' : badgeCount}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </nav>

      {sheet && (
        <div className="dock-sheet" role="dialog" aria-modal="true">
          <div className="dock-sheet__backdrop" onClick={closeSheet} />
          <div className="dock-sheet__panel">
            <header className="dock-sheet__header">
              <div>
                <div className="dock-sheet__title">{sheetTitle}</div>
                <div className="dock-sheet__subtitle">{sheetSubtitle}</div>
              </div>
              <button className="btn icon" onClick={closeSheet} aria-label="Close updates">✕</button>
            </header>
            <div className="dock-sheet__body">
              {renderSheetSections()}
            </div>
            <footer className="dock-sheet__footer">
              <button className="btn btn-outline" onClick={handleMarkAll}>
                Mark all read
              </button>
              {sheet === 'provider-appointments' && (
                <button className="btn btn-primary" onClick={goToCalendar}>
                  Open calendar
                </button>
              )}
              {sheet !== 'provider-appointments' && (
                <button className="btn btn-primary" onClick={goToRequests}>
                  Open dashboard
                </button>
              )}
            </footer>
          </div>
        </div>
      )}
    </>
  )
}
